import React, { useContext } from 'react';

export const ImageCacheContext = React.createContext<
  Record<string, Promise<void> | boolean | Error>
>({});

export function useImagePreload(urls: string[]): void {
  const cache = useContext(ImageCacheContext);

  if (!cache || urls.length === 0) return;

  for (const url of urls) {
    if (!cache[url]) {
      const promise = new Promise<void>((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          cache[url] = true;
          resolve();
        };

        img.onerror = () => {
          cache[url] = new Error(`An error occurred loading the image: "${url}"`);
          reject();
        };

        img.src = url;
      });
      cache[url] = promise;
    }
  }
  const cachedValues = urls.map(src => cache[src]);

  // 1. If not everything is settled, throw an aggregate promise with the remaining promises
  if (cachedValues.some(c => isPromise(c)))
    throw Promise.all(cachedValues.filter(c => isPromise(c)) as Promise<void>[]);

  // 2. If everything is settled and there are some errors, throw aggregate error
  if (cachedValues.some(c => c instanceof Error)) {
    console.warn(`${cachedValues.filter(c => c instanceof Error).length} images failed to load`);
  }

  // 3. If we get to this point, everything is loaded!
  return;
}

export const isPromise = (promise: unknown): promise is Promise<unknown> =>
  !!promise &&
  typeof promise === 'object' &&
  typeof (promise as Promise<unknown>).then === 'function';
